import { z } from 'zod';
import { objectIdSchema } from './common.js';
import { absoluteNutrientsSchema, nutritionBasisSchema, nutrientsSchema } from './nutrients.js';
import { foodRefSchema, quantitySchema } from './diary.js';
import { servingSchema } from './food.js';
/**
 * Recipes: a named bundle of foods that can be logged as a single item.
 *
 * The point is to answer "I ate 350 g of the chilli I cooked". That requires a
 * per-100 g figure for the finished dish, which is derived from the components
 * rather than entered: total nutrients divided by total weight.
 *
 * Components are stored *resolved* — each keeps its own per-100 snapshot and
 * computed totals, exactly like a diary entry — so editing or deleting a
 * catalog food later cannot silently restate what a saved recipe contains.
 */
/** One ingredient, as stored. */
export const recipeComponentSchema = z.object({
    /** Null when the ingredient was inline (a label scan, say). */
    foodId: objectIdSchema.nullable(),
    name: z.string(),
    brand: z.string().nullable(),
    basis: nutritionBasisSchema,
    quantity: quantitySchema,
    /** Amount in the component's own basis unit (g for solids, ml for liquids). */
    baseAmount: z.number(),
    /** Contribution to the recipe's mass, in grams, via density where needed. */
    grams: z.number(),
    per100: nutrientsSchema,
    totals: absoluteNutrientsSchema,
});
/** One ingredient, as submitted. Mirrors how the diary accepts a food. */
export const recipeComponentInputSchema = z.object({
    food: foodRefSchema,
    quantity: quantitySchema,
});
export const createRecipeSchema = z.object({
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(1000).nullable().optional(),
    /**
     * How many portions the finished dish yields. Optional: with it set, the
     * recipe can be logged by portion as well as by weight.
     */
    servings: z.number().min(0.5).max(100).nullable().optional(),
    components: z.array(recipeComponentInputSchema).max(100).default([]),
});
export const updateRecipeSchema = createRecipeSchema
    .partial()
    .refine((value) => Object.keys(value).length > 0, 'no fields to update');
export const recipeSchema = z.object({
    id: objectIdSchema,
    name: z.string(),
    description: z.string().nullable(),
    servings: z.number().nullable(),
    components: z.array(recipeComponentSchema),
    /** Sum of component masses. Zero for an empty recipe. */
    totalGrams: z.number(),
    /** Absolute nutrients of the whole dish. */
    totals: absoluteNutrientsSchema,
    /**
     * Per 100 g of finished dish — what makes a recipe loggable like any other
     * food. All zeroes while the recipe has no components.
     */
    nutrients: nutrientsSchema,
    /** Present only when `servings` is set; lets a portion be logged directly. */
    serving: servingSchema.nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
//# sourceMappingURL=recipe.js.map